/**
 * Passing a message on to other people.
 *
 * Pick as many as you like and send once - forwarding one message to four
 * friends should not mean opening four conversations. The original is quoted
 * rather than copied blindly, so whoever receives it can see who said it.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Message, PublicUser } from '@/lib/types';
import { api, endpoints } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { Button, Modal, useToast } from './ui';
import { Icon } from '@/icons/Icon';

export function Forward({ msg, onClose }: { msg: Message; onClose: () => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const people = (friends.data?.friends ?? []).map((f) => f.user);

  const q = filter.trim().toLowerCase();
  const shown = q
    ? people.filter((u) => (u.username + ' ' + u.displayName).toLowerCase().includes(q))
    : people;

  function toggle(id: string) {
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  }

  async function send() {
    if (!picked.length) return;
    setBusy(true);

    /*
     * One request per recipient, run together rather than in a queue - four
     * friends should take as long as one. A failure on one does not stop the
     * rest; the toast reports how many actually went.
     */
    const targets = people.filter((u) => picked.includes(u.id));

    const words = note.trim();
    const results = await Promise.allSettled(targets.map((u) =>
      api.post('/api/messages', {
        scope: 'dm',
        username: u.username,
        /*
         * The original words, unchanged.
         *
         * Who wrote them travels as data rather than being pasted into the
         * text, so the far end draws a label. It used to arrive as the literal
         * sentence "Forwarded from X:" followed by a "> " quote, sitting in the
         * conversation as if somebody had typed it.
         */
        body: msg.body,
        forwardedFrom: msg.userId,
        attachments: (msg.attachments ?? []).map((a) => a.id)
      })
        /* What you typed with it goes straight after, as your own message.
           It used to be read into a box and then never sent at all. */
        .then(() => (words ? api.post('/api/messages', { scope: 'dm', username: u.username, body: words }) : null))
    ));

    const sent = results.filter((r) => r.status === 'fulfilled').length;
    setBusy(false);
    /* The people you just wrote to go to the top of Chat now, not on the
       next refresh. */
    void queryClient.invalidateQueries({ queryKey: ['dms'] });
    void queryClient.invalidateQueries({ queryKey: ['groups'] });

    if (!sent) { toast('Could not forward that', 'bad'); return; }
    toast(sent === targets.length
      ? 'Forwarded to ' + sent
      : 'Forwarded to ' + sent + ' of ' + targets.length, sent === targets.length ? 'good' : 'warn');
    onClose();
  }

  return (
    <Modal
      title="Forward message"
      subtitle="Pick as many people as you like."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={!picked.length || busy} onClick={() => void send()}>
            {busy ? 'Sending…' : picked.length ? 'Send to ' + picked.length : 'Send'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* what is being passed on */}
        <div className="rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3">
          <div className="flex items-center gap-2 text-[11.5px] text-mute">
            <Icon name="reply" size={13} className="flex-none -scale-x-100" />
            {msg.user?.displayName ?? 'Someone'}
          </div>
          <p className="mt-1 line-clamp-3 text-[13px] text-dim">
            {msg.body || (msg.attachments?.length ? 'an attachment' : '')}
          </p>
        </div>

        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Say something with it (optional)"
          maxLength={300}
          className="h-[38px] w-full rounded-[var(--radius)] border border-line bg-surface-inset px-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
        />

        <div className="relative">
          <Icon name="search" size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-mute" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search friends"
            className="h-[38px] w-full rounded-[var(--radius)] border border-line-soft bg-surface-inset pr-3 pl-9 text-[13px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </div>

        <div className="max-h-[280px] min-h-[120px] overflow-y-auto rounded-[var(--radius)] border border-line-soft">
          {shown.length ? shown.map((u) => (
            <Row key={u.id} user={u} on={picked.includes(u.id)} onToggle={() => toggle(u.id)} />
          )) : (
            <p className="px-3 py-8 text-center text-[12.5px] text-mute">
              {q ? 'Nobody matches that.' : 'Add a friend first.'}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Row({ user, on, onToggle }: { user: PublicUser; on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
        on ? 'bg-accent-soft' : 'hover:bg-surface-2'
      )}
    >
      <Avatar user={user} size={30} presence />
      <UserName user={user} profile={false} className="min-w-0 flex-1 truncate text-[13px] font-semibold" />
      <span
        className={cn(
          'grid h-[18px] w-[18px] flex-none place-items-center rounded-[calc(6px*var(--radius-mul,1))] border transition-colors',
          on ? 'border-accent bg-accent text-accent-fg' : 'border-line'
        )}
      >
        {on ? <Icon name="check" size={12} stroke={3} /> : null}
      </span>
    </button>
  );
}
